'use client';

import React, { Component, ErrorInfo, ReactNode, useCallback } from 'react';
import ErrorDisplay from './ErrorDisplay';

export const RELOAD_KEY = 'chunkErrorReload';
export const RELOAD_THROTTLE = 10000;

/**
 * Detects if an error is a ChunkLoadError (dynamic import failure).
 * These typically occur when the client attempts to load a page using outdated, cached chunks.
 * NextJS does not invalidate these chunks automatically based on the deterministic hash.
 * Why? Because Vercel did not have the foresight to imagine a world where build files change over time.
 */
export const isChunkLoadError = (error: Error): boolean => {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('error loading dynamically imported module')
  );
};

/* =============================================================================
 * Error Boundary Core - React limitations stipulate a Class is *required* here.
 * ============================================================================= */

interface ErrorBoundaryCoreProps {
  children: ReactNode;
  fallbackRender: (props: { error: Error; resetError: () => void }) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryCoreState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryCore extends Component<ErrorBoundaryCoreProps, ErrorBoundaryCoreState> {
  constructor(props: ErrorBoundaryCoreProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryCoreState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo);
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return this.props.fallbackRender({
        error: this.state.error,
        resetError: this.resetError,
      });
    }
    return this.props.children;
  }
}

// ============================================================================
// Error Boundary Wrapper
// ============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
}

export const ErrorBoundaryProvider = ({ children }: ErrorBoundaryProps) => {
  const handleError = useCallback((error: Error) => {
    // Handle ChunkLoadError by reloading the page
    if (isChunkLoadError(error)) {
      const now = Date.now();

      // We want reload throttling to occur on a per-page basis, so we append a page identifier to the default reload key.
      const uniqueKey = `${RELOAD_KEY}-${window.location.pathname}`;
      const lastReload = localStorage.getItem(uniqueKey);

      let shouldReload = false;

      if (lastReload === null) {
        // We have never reloaded before. Flag, set timestamp, reload.
        localStorage.setItem(uniqueKey, `${now}`);
        shouldReload = true;
      } else {
        const timeElapsed = now - parseInt(lastReload);
        // We have reloaded at least once before. Determine if RELOAD_THROTTLE ms has passed.
        if (timeElapsed > RELOAD_THROTTLE) {
          localStorage.setItem(uniqueKey, `${now}`);
          shouldReload = true;
        }
      }

      if (shouldReload) {
        window.location.reload();
      }
    }
  }, []);

  const renderFallback = useCallback(({ error, resetError }: { error: Error; resetError: () => void }) => {
    // ChunkLoadError that didn't auto-reload (due to throttle)
    if (isChunkLoadError(error)) {
      return (
        <ErrorDisplay
          title="Update Available"
          message="A new version of the application is available. Please reload the page to continue."
          error={error}
          onRetry={() => {
            sessionStorage.removeItem(RELOAD_KEY);
            window.location.reload();
          }}
          retryLabel="Reload Page"
        />
      );
    }

    // Generic error
    return (
      <ErrorDisplay
        title="Something went wrong"
        message="An unexpected error occurred. Please try again or contact support if the problem persists."
        error={error}
        onRetry={resetError}
        retryLabel="Try Again"
      />
    );
  }, []);

  return (
    <ErrorBoundaryCore fallbackRender={renderFallback} onError={handleError}>
      {children}
    </ErrorBoundaryCore>
  );
};

export default ErrorBoundaryProvider;
